import Text from '@deprecated-ui/common/Text';
import Flex from '@deprecated-ui/common/Flex';
import numeral from 'numeral';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { convertToSlug } from 'utils/general';
import { clevertapEvent } from 'utils/clevertap';
import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import companyUtils from 'utils/company';
import { Tooltip } from 'antd';
import { ModalFollower } from 'global/Moderation/Modals';
import { TAB_LIST_NAME } from 'features/company/constants';
import If from 'global/ErrorHandlers/If';
import { useQuery } from '@tanstack/react-query';
import watchlistApi from 'lib/api/watchlist';
import { errorHandler } from 'utils/errors';
import Image from 'global/Image';
import {
  CountryFlag,
  ImagePlaceholder,
  Symbol,
  CountryFlagContainer,
  NotationCountryFlagContainer,
  Badges,
  CompanyName,
  CompanyNameContainer,
  CompanyTags,
  ImageWrapper,
  CompanyInfoWrapper,
  CompanyHeader,
  ExpandButtonToggleMobile,
  ButtonBuy,
  CompanyIdentity,
  ButtonContainer,
  ButtonContainerMobile,
  CountryFlagContainerMobile,
  ModalFollowing,
  CatalogButton,
} from '../../Header/styled';
import FollowCompanyButton from '../../Header/FollowCompanyButton';
import { CompanyInfoType } from '../types';
import { FollowerText } from './styled';

const { cleanSymbol, isReksaDana } = companyUtils;

interface CompanyChartProps {
  symbol: string;
  company: CompanyInfoType;
  isGuestCompany?: boolean;
  onClickLink?: (...args) => void;
  onClickFollow?: (...args) => void;
  onClickBuyButton?: (...args) => void;
  onExpandToggle?: (...args) => void;
}

function Header({
  symbol = '',
  company,
  isGuestCompany = false,
  onClickLink,
  onClickFollow,
  onClickBuyButton,
  onExpandToggle,
}: CompanyChartProps) {
  const {
    name,
    icon_url,
    country = 'ID',
    catalogs = [],
    exchange,
    company_type = '',
    id,
    tradable,
    tags,
  } = company;

  const [isIconAvailable, setIsIconAvailable] = useState<boolean>(!!icon_url);
  const [isFollowerVisible, setIsFollowerVisible] = useState<boolean>(false);
  const [isFollowCompanyVisible, setIsFollowCompanyVisible] =
    useState<boolean>(false);
  const [isFollowing, setIsFollowing] = useState<boolean>(false);

  const isTradeable = tradable === 1;
  const POPUP_COUNTRY_FLAG_TEXT = `This stock is traded on ${exchange} exchange`;

  const buttonBuyRef = useRef<HTMLButtonElement>(null);
  const router = useRouter();

  const { data: followerData } = useQuery({
    queryKey: ['get-company-follower-info', symbol, isFollowing],
    queryFn: async () => {
      try {
        const response = await watchlistApi.getCompanyFollowerInfo(symbol);

        if (!response.data) {
          throw new Error(
            'Something is terribly wrong, please contact our administrator',
          );
        }

        const { data } = response;

        if (data.data.length === 1) {
          setIsFollowing(data.data[0].is_followed);
          return data.data[0];
        }
        throw new Error('No Data');
      } catch (error) {
        errorHandler(error, 'Failed to get detail company chart');
        return undefined;
      }
    },
    retry: false,
    enabled: !isGuestCompany,
  });

  const { total_followers: followers } = followerData || {
    total_followers: 0,
  };

  // If not user Day Trade exclude catalog daytrade
  const filteredCatalogs = useMemo(
    () =>
      catalogs.filter(
        ({ company_symbol }) => company_symbol?.toLowerCase() !== 'daytrade',
      ),
    [catalogs],
  );

  const onImgError = () => {
    setIsIconAvailable(false);
  };

  const formattedCountry = country.toLowerCase();
  const IMAGE_URL = `/img/flags/${formattedCountry}.png`;

  const generateCatalogUrl = (itemSector, itemSubSector) => {
    if (!itemSector) return '';

    return `/catalog/${convertToSlug(itemSubSector)}`;
  };

  const onClickCatalogsLink = (event, companySymbol: string) => {
    if (isGuestCompany) {
      event.preventDefault();
      onClickLink(true);
    } else {
      clevertapEvent('Company Action', {
        Action: 'emiten_group',
        Group: companySymbol,
      });
    }
  };

  const handleSuccessFollow = (following) => {
    setIsFollowing(following);
  };

  const handleToggleFollow = (following) => {
    if (isGuestCompany) {
      onClickFollow();
      return false;
    }
    handleSuccessFollow(following);
    return true;
  };

  const handleOpenModal = useCallback(() => {
    setIsFollowCompanyVisible(true);
  }, [isFollowCompanyVisible]);

  const handleCancelModal = useCallback(() => {
    setIsFollowCompanyVisible(false);
  }, [isFollowCompanyVisible]);

  const handleClickBuyButton = useCallback(() => {
    buttonBuyRef?.current?.click();
  }, [buttonBuyRef]);

  useEffect(() => {
    if (router?.query?.param?.[0] === TAB_LIST_NAME.BUY) {
      handleClickBuyButton();
    }
  }, [handleClickBuyButton, router?.query?.param]);

  const hasMaturedTags = useMemo(() => {
    if (tags) {
      return tags?.some((tag) => tag?.name?.toLowerCase() === 'matured');
    }
    return false;
  }, [tags]);

  const renderCatalogsLink = (catalogue) => {
    const subSectorObject = {
      'Properti & Real Estate': 'Properti dan Real Estate',
      Commodities: 'Commodity',
    };
    const companySymbol = catalogue.catalog_name || catalogue.company_symbol;
    const companySymbolAlias =
      subSectorObject[companySymbol] || catalogue.company_symbol;
    const companyType = catalogue.company_type;

    let catalogUrl = '';

    if (companyType === 'sub_sector') {
      catalogUrl = generateCatalogUrl(companySymbol, companySymbolAlias);
    } else {
      catalogUrl = `/catalog/${companyType}/${companySymbolAlias}`;
    }

    return (
      <Link href={catalogUrl} passHref>
        <CatalogButton
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => onClickCatalogsLink(e, companySymbol)}
        >
          {companySymbol}
        </CatalogButton>
      </Link>
    );
  };

  const renderSymbol = () => (
    <Symbol>{cleanSymbol(symbol.toUpperCase())}</Symbol>
  );

  const renderCountryFlag = () => (
    <CountryFlagContainer>
      <Tooltip
        className="desktop"
        placement="top"
        title={POPUP_COUNTRY_FLAG_TEXT}
      >
        <CountryFlag
          key="country-flag-desktop"
          src={IMAGE_URL}
          alt={`${country} flag`}
        />
      </Tooltip>
    </CountryFlagContainer>
  );

  const renderCountryFlagMobile = () => (
    <CountryFlagContainerMobile>
      <CountryFlag
        className="mobile"
        key="country-flag-mobile"
        src={IMAGE_URL}
        alt={`${country} flag`}
      />
    </CountryFlagContainerMobile>
  );

  const renderButtons = () => (
    <ButtonContainer>
      {!isGuestCompany && (
        <>
          <FollowerText as="span" onClick={() => setIsFollowerVisible(true)}>
            {numeral(followers).format('0,0')} followers
          </FollowerText>
          <FollowCompanyButton
            isFollowed={isFollowing}
            onClick={handleOpenModal}
          />
        </>
      )}
      <If
        condition={isTradeable && !isReksaDana(company_type) && !hasMaturedTags}
      >
        <ButtonBuy
          data-cy="company-buy-button"
          onClick={onClickBuyButton}
          ref={buttonBuyRef}
        >
          <Text weight="bolder" align="center" color="inherit">
            Buy
          </Text>
        </ButtonBuy>
      </If>
    </ButtonContainer>
  );

  const renderButtonsMobile = () => (
    <ButtonContainerMobile>
      {!isGuestCompany && (
        <>
          <FollowerText as="span" onClick={() => setIsFollowerVisible(true)}>
            {numeral(followers).format('0,0')} followers
          </FollowerText>
          <FollowCompanyButton
            isFollowed={isFollowing}
            onClick={handleOpenModal}
          />
        </>
      )}
      <If condition={isTradeable && !isReksaDana(company_type)}>
        <ButtonBuy onClick={onClickBuyButton}>
          <Text weight="bolder" align="center" color="inherit">
            Buy
          </Text>
        </ButtonBuy>
      </If>
    </ButtonContainerMobile>
  );

  const renderCompanyLogo = () => (
    <ImageWrapper align="center">
      <Flex align="center">
        {isIconAvailable ? (
          <Image
            className="company-header-icon"
            src={icon_url}
            onError={onImgError}
            alt={symbol}
            width="54px"
            height="54px"
          />
        ) : (
          <ImagePlaceholder>
            <small>{!!symbol && symbol.toUpperCase().slice(0, 1)}</small>
          </ImagePlaceholder>
        )}
        {!!country && renderCountryFlagMobile()}
      </Flex>
      {renderButtonsMobile()}
    </ImageWrapper>
  );

  const renderMaturedBadges = () =>
    tags.map(
      (item) =>
        item.name === 'MATURED' && (
          <Badges key={item.name}>
            <span>{item.name}</span>
          </Badges>
        ),
    );

  const renderCompanyName = () => (
    <CompanyName>
      <h1>{name}</h1>
    </CompanyName>
  );

  const renderCompanyTags = () => (
    <CompanyTags>
      {filteredCatalogs.slice(0, 6).map(
        (catalogue) =>
          catalogue.show && (
            <span key={catalogue.company_symbol} className="catalogs">
              {renderCatalogsLink(catalogue)}
            </span>
          ),
      )}
    </CompanyTags>
  );

  const renderExpandButtonToggle = () => (
    <ExpandButtonToggleMobile onExpand={onExpandToggle} />
  );

  const renderFollowerModal = () => (
    <ModalFollower
      symbol={symbol}
      companyId={id}
      visible={isFollowerVisible}
      onCancel={() => setIsFollowerVisible(false)}
    />
  );

  const renderFollowModal = () => (
    <ModalFollowing
      visible={isFollowCompanyVisible}
      onCancel={handleCancelModal}
      companyId={id}
      onFollow={handleToggleFollow}
    />
  );

  const renderCompanyHeader = () => (
    <CompanyHeader>
      <CompanyIdentity>
        {/* @ts-expect-error */}
        <CompanyInfoWrapper $isIconAvailable={isIconAvailable}>
          {renderCompanyLogo()}
          <CompanyNameContainer>
            {renderSymbol()}
            <NotationCountryFlagContainer>
              {!!country && renderCountryFlag()}
              {renderMaturedBadges()}
            </NotationCountryFlagContainer>
            {renderCompanyName()}
            {renderCompanyTags()}
          </CompanyNameContainer>
          {renderExpandButtonToggle()}
        </CompanyInfoWrapper>
        {renderButtons()}
      </CompanyIdentity>
      {!isGuestCompany && (
        <>
          {renderFollowerModal()}
          {renderFollowModal()}
        </>
      )}
    </CompanyHeader>
  );

  return renderCompanyHeader();
}

export default Header;
